/**
 * Contributions + Accruals Calculator
 *
 * For JMH Capital (isJMH) - event-based calculation where value equals
 * sum of Contribution events plus sum of Gain (Loss) Accrual events.
 *
 * From index.js lines 6039-6057:
 *   userEvents.forEach(function (e, index) {
 *     let clientEvent = clientEvents.find(obj => obj.nrid == e.nrid);
 *     if (clientEvent) {
 *       if (clientEvent.eventType == "Contribution") sumContributions += Number(e.grossDollars);
 *       else if (clientEvent.eventType == "Gain (Loss) Accrual") sumAccruals += Number(e.grossDollars);
 *     }
 *   });
 *   latestValue[key] = sumContributions + sumAccruals;
 *
 * Data Source: User events matched with client event types
 */

import { basisForBreakdown } from '../helpers/equity-calculation-utils.js';

const ContributionsAccrualsCalculator = {
	methodId: 'contributions_accruals',
	displayName: 'Contributions + Accruals',
	requiredDataSources: ['contributions', 'accruals', 'distributions'],

	// Graph configuration - line graph shows cumulative contributions + accruals over time
	graphConfig: {
		recommendedType: 'time_series_line',
		timeSeriesStrategy: 'fund_capital_account',
		supportsTimeSeries: true,
	},

	/**
	 * Calculate value from contributions plus gain/loss accruals,
	 * plus net transfers (gift transactions), optionally including distributions.
	 *
	 * Transfers are included by default because CCA Gift transactions between
	 * users move real balance between accounts — excluding them causes the
	 * headline to diverge from the graph/equity card for recipients of gifts
	 * (see #5172). Internal transfers within a consolidated group already
	 * net to zero in holdings.transfers, so consolidated totals are unchanged.
	 *
	 * @param {Object} input
	 * @param {Object} input.holdings - { contributions, accruals, distributions, transfers } by class
	 * @param {string[]} input.shareClasses - Array of share class keys
	 * @param {Object} [input.options]
	 * @param {boolean} [input.options.includeDistributions=false] - Add distributions to value
	 * @param {boolean} [input.options.includeTransfers=true] - Add net transfers to value
	 * @returns {Object} CalculationResult
	 */
	calculate: function (input) {
		const holdings = input.holdings;
		const shareClasses = input.shareClasses || Object.keys(holdings.contributions || {});
		const includeDistributions = input.options?.includeDistributions === true;
		const includeTransfers = input.options?.includeTransfers !== false;

		let totalContributions = 0;
		let totalAccruals = 0;
		let totalDistributions = 0;
		let totalTransfers = 0;
		const breakdown = [];

		shareClasses.forEach(function (key) {
			const contributions = holdings.contributions[key] || 0;
			const accruals = holdings.accruals[key] || 0;
			const distributions = includeDistributions ? (holdings.distributions[key] || 0) : 0;
			const transfers = includeTransfers ? (holdings.transfers?.[key] || 0) : 0;

			const value = contributions + accruals + distributions + transfers;

			totalContributions += contributions;
			totalAccruals += accruals;
			totalDistributions += distributions;
			totalTransfers += transfers;

			breakdown.push({
				classKey: key,
				className: key,
				value: value,
				contributions: contributions,
				accruals: accruals,
				distributions: distributions,
				transfers: transfers,
				basis: basisForBreakdown(holdings, key),
			});
		});

		return {
			totalValue: totalContributions + totalAccruals + totalDistributions + totalTransfers,
			totalContributions: totalContributions,
			totalAccruals: totalAccruals,
			totalDistributions: totalDistributions,
			totalTransfers: totalTransfers,
			breakdown: breakdown,
			fundMetrics: null,
			metadata: {
				methodUsed: this.methodId,
				calculationDate: new Date(),
			},
		};
	},
};

export { ContributionsAccrualsCalculator };
